import { Entity } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation, useSearchParams } from 'react-router';

import { FlagIcon } from '~/components/flag-icon/FlagIcon';
import { SectionTitle } from '~/components/section-title/SectionTitle';
import { getKnowledgeGraphSearchParams } from '~/modules/overview-knowledge-graph/utils/knowledge-graph-utils';
import { useCompanyCountryExposure } from '~/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { appPaths } from '~/routes/app-paths';
import { GeographicExposureDrawer } from '~/widgets/knowledge-graph-widget/components/geographic-exposure-tab/drawers/GeographicExposureDrawer';
import { useGeographicDrawer } from '~/widgets/knowledge-graph-widget/components/geographic-exposure-tab/drawers/useGeographicDrawer';
import { TopRankIcon } from '~/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import { KnowledgeGraphTabType } from '~/widgets/knowledge-graph-widget/KnowledgeGraphWidget';

import { LoadingCell } from '../shared/loading-cell/LoadingCell';
import styles from './GeographicExposureKnowledgeGraph.module.scss';

export interface GeographicExposureKnowledgeGraphProps {
  entity: Entity;
}

export const GeographicExposureKnowledgeGraph = ({
  entity,
}: GeographicExposureKnowledgeGraphProps) => {
  const { t } = useTranslation(['widget', 'countries']);
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const {
    data: countryExposureData = [],
    isFetching,
    isError,
  } = useCompanyCountryExposure({
    entityTag: entity.tag,
  });

  if (isError || (!countryExposureData.length && !isFetching)) {
    throw new Error('Failed to load GeographicExposureKnowledgeGraph');
  }

  const topCountries = [...countryExposureData]
    .sort((a, b) => a.relationship.rank - b.relationship.rank)
    .slice(0, 3);

  const isAssetOverviewPage = matchPath(
    { path: appPaths.assetOverview, end: false, caseSensitive: false },
    location.pathname
  );

  const getTooltip = (context: string) => {
    if (isAssetOverviewPage) {
      return undefined;
    }

    /** i18next-extract-mark-context-next-line ["geographicExposure"] */
    return t('widget:assetOverview:knowledgeGraphTooltipRevamp', {
      context: context,
    });
  };

  const knowledgeGraphSearchParams = getKnowledgeGraphSearchParams({
    entityTag: entity.tag,
    searchParams,
    subtab: KnowledgeGraphTabType.GeographicExposure,
  }).toString();
  const linkToKnowledgeGraph = `${appPaths.assetOverview}?${knowledgeGraphSearchParams}`;
  const { openCountry } = useGeographicDrawer();

  return (
    <div>
      <SectionTitle
        title={t('widget:assetOverview.knowledgeGraph.subtab.geographic')}
        variant="small"
        linkTo={linkToKnowledgeGraph}
        tooltip={getTooltip('geographicExposure')}
      />
      {isFetching ? (
        <div>
          {Array.from({ length: 3 }).map((_, idx) => (
            <div className={styles.countryRow} data-country-row key={idx}>
              <LoadingCell />
            </div>
          ))}
        </div>
      ) : (
        <div>
          {topCountries.map(country => {
            const countryName = t(`countries:${country.code}`, {
              defaultValue: country.code,
            });

            return (
              <div
                className={styles.countryRow}
                data-country-row
                key={country.code}
                onClick={() => openCountry(country.code)}
              >
                <div className={styles.countryNameWrapper}>
                  <FlagIcon
                    countryCode={country.code}
                    customSize={32}
                    variant="circular"
                  />
                  <span className={styles.countryName}>{countryName}</span>
                </div>
                <TopRankIcon rank={country.relationship.rank} iconSize={28} />
              </div>
            );
          })}
        </div>
      )}
      <GeographicExposureDrawer entity={entity} />
    </div>
  );
};
